import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { FormControlLabel, Grid, Switch } from '@material-ui/core'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import {
  Paper,
  SpacingContainer,
} from '../../../../components/common/Containers'
import Typography from '../../../../components/common/Typography'
import ConfirmDialog from '../../../../components/generics/Confirm/Dialog'
import { useTrans } from '../../../../services/i18n'
import { useAlertContext } from '../../../../contexts/AlertContext'
import { selectUserPermissions, isEnabled } from '../../../../selectors'
import connect from '../../connect'

const COUNTERS = ['attempts', 'logins', 'failures', 'calls']

function describeMode(lanes, trans) {
  if (!lanes?.critical || !lanes?.nonCritical) {
    return trans('salesforce-lanes-unavailable')
  }
  const { critical, nonCritical } = lanes
  if (nonCritical.mode === 'isolated') {
    return `${trans('salesforce-lanes-isolated')}: ${trans(
      'critical-lane',
    )} ${critical.username}, ${trans('non-critical-lane')} ${
      nonCritical.username
    }`
  }
  if (nonCritical.mode === 'partial') {
    if (nonCritical.shared === 'user') {
      return trans('salesforce-lanes-partial-user')
    }
    if (nonCritical.shared === 'app') {
      return trans('salesforce-lanes-partial-app')
    }
    return trans('salesforce-lanes-partial')
  }
  return `${trans('salesforce-lanes-shared')} ${critical.username} ${trans(
    'lane-no-isolation',
  )}`
}

const laneShape = PropTypes.shape({
  lane: PropTypes.string,
  mode: PropTypes.string,
  username: PropTypes.string,
  identity: PropTypes.string,
  shared: PropTypes.string,
  counters: PropTypes.shape({
    attempts: PropTypes.number,
    logins: PropTypes.number,
    failures: PropTypes.number,
    calls: PropTypes.number,
  }),
})

function stateLabelFor(loaded, enabled, trans) {
  if (!loaded) return trans('lane-unknown')
  return enabled ? trans('lane-enabled') : trans('lane-disabled')
}

function LaneColumn({ title, lane, trans }) {
  return (
    <Grid item sm={6} xs={12}>
      <Typography weight={800}>{title}</Typography>
      {lane ? (
        <>
          <Typography variant="body2">
            {trans('lane-identity')}: {lane.identity}
          </Typography>
          {COUNTERS.map(counter => (
            <Typography key={counter} variant="body2">
              {trans(`lane-${counter}`)}:{' '}
              {lane.counters ? lane.counters[counter] : 0}
            </Typography>
          ))}
        </>
      ) : (
        <Typography variant="body2">{trans('lane-unknown')}</Typography>
      )}
    </Grid>
  )
}

LaneColumn.propTypes = {
  title: PropTypes.string.isRequired,
  lane: laneShape,
  trans: PropTypes.func.isRequired,
}

const SalesforceIntegration = ({
  authUser,
  salesforceLanes,
  getSalesforceLanes,
  updateSalesforceLane,
}) => {
  const { trans } = useTrans()
  const { successAlert, errorAlert } = useAlertContext()
  const permissions = useSelector(selectUserPermissions)
  const canEdit = isEnabled(permissions, 'global-settings', '', 'edit')
  const isAdmin = authUser?.role === 'admin'
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pending, setPending] = useState(false)

  const loaded = Boolean(salesforceLanes?.loaded)
  const enabled = loaded ? salesforceLanes.enabled === true : null
  const lanes = salesforceLanes?.lanes ?? null

  useEffect(() => {
    if (isAdmin) getSalesforceLanes()
  }, [getSalesforceLanes, isAdmin])

  const save = useCallback(
    nextEnabled => {
      setPending(true)
      return updateSalesforceLane({ enabled: nextEnabled })
        .then(res => {
          if (res?.payload?.success) {
            successAlert(trans('salesforce-lane-updated'))
          } else {
            errorAlert(trans('salesforce-lane-update-error'))
          }
        })
        .catch(() => errorAlert(trans('salesforce-lane-update-error')))
        .finally(() => {
          setPending(false)
          setConfirmOpen(false)
          getSalesforceLanes()
        })
    },
    [updateSalesforceLane, getSalesforceLanes, successAlert, errorAlert, trans],
  )

  const onToggle = event => {
    if (event.target.checked) {
      save(true)
    } else {
      setConfirmOpen(true)
    }
  }

  const stateLabel = stateLabelFor(loaded, enabled, trans)

  if (!isAdmin) return null

  return (
    <Paper component={SpacingContainer} py={3} px="5sm3">
      <Grid container alignItems="center" spacing={3}>
        <Grid item xs zeroMinWidth>
          <Typography
            style={{
              paddingBottom: '1rem',
              alignItems: 'center',
              display: 'flex',
            }}
            variant="h4"
            weight="500"
            fontFamily="lexend"
          >
            <SyncAltIcon /> {trans('salesforce-integration')}
          </Typography>
        </Grid>
      </Grid>

      <Typography variant="body2" weight={600} gutter="bottomSpace">
        {describeMode(lanes, trans)}
      </Typography>

      <Typography weight={800} style={{ marginTop: '1rem' }}>
        {trans('lane-counts-today')}
        {salesforceLanes?.date ? ` (${salesforceLanes.date})` : ''}
      </Typography>
      <Grid container spacing={2} style={{ marginBottom: '1rem' }}>
        <LaneColumn
          title={trans('critical-lane')}
          lane={lanes?.critical ?? null}
          trans={trans}
        />
        <LaneColumn
          title={trans('non-critical-lane')}
          lane={lanes?.nonCritical ?? null}
          trans={trans}
        />
      </Grid>

      <FormControlLabel
        value="end"
        control={
          <Switch
            color="primary"
            checked={enabled === true}
            disabled={!loaded || !canEdit || pending}
            onChange={onToggle}
            inputProps={{
              'aria-label': trans('non-critical-salesforce-traffic'),
            }}
          />
        }
        label={
          <Typography weight={600} variant="body2">
            {trans('non-critical-salesforce-traffic')}: {stateLabel}
          </Typography>
        }
        labelPlacement="start"
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => save(false)}
        pending={pending}
        hideIcon
        title={trans('disable-non-critical-salesforce-traffic')}
        text={trans('disable-non-critical-salesforce-traffic-text')}
        confirmTitle={trans('disable-non-critical-salesforce-traffic-confirm')}
      />
    </Paper>
  )
}

SalesforceIntegration.propTypes = {
  authUser: PropTypes.shape({ role: PropTypes.string }),
  salesforceLanes: PropTypes.shape({
    loaded: PropTypes.bool,
    enabled: PropTypes.bool,
    date: PropTypes.string,
    lanes: PropTypes.shape({ critical: laneShape, nonCritical: laneShape }),
  }),
  getSalesforceLanes: PropTypes.func.isRequired,
  updateSalesforceLane: PropTypes.func.isRequired,
}

export default connect(SalesforceIntegration)
